import React from "react";
import type { EntityProfileNavigationSection } from "../components/EntityProfileNavigation";
import {
  type ProfileWindowEntity,
  resolveInitialSection,
  setLastOpenedSection,
} from "../../profileWindowSettings/profileWindowSettings.store";

type Params<TSection extends EntityProfileNavigationSection> = {
  /**
   * Which window this is, so the "open window on section" preference can be
   * applied and the last visited section recorded. Omit in hosts that have no
   * preference to honour (the workbench).
   */
  entity?: ProfileWindowEntity;
  sectionIdPrefix: string;
  sections: TSection[];
};

function useEntityProfileSectionNavigation<TSection extends EntityProfileNavigationSection>(
  params: Params<TSection>
) {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const scrollFrameRef = React.useRef<number | null>(null);
  const [activeSectionIndex, setActiveSectionIndex] = React.useState(0);
  const [activeSubsectionIndex, setActiveSubsectionIndex] = React.useState(0);
  const activeSection = params.sections[activeSectionIndex] ?? params.sections[0];

  const getSectionScrollTop = React.useCallback(
    (scrollContainer: HTMLDivElement, sectionElement: HTMLElement) =>
      sectionElement.getBoundingClientRect().top -
      scrollContainer.getBoundingClientRect().top +
      scrollContainer.scrollTop,
    []
  );

  const syncActiveSubsectionWithScroll = React.useCallback(() => {
    const scrollContainer = contentRef.current;

    if (scrollContainer === null || activeSection === undefined) {
      return;
    }

    const nextActiveSubsectionIndex = activeSection.subsections.reduce(
      (activeIndex, subsection, index) => {
        const sectionElement = document.getElementById(
          `${params.sectionIdPrefix}-${subsection.id}`
        );

        if (sectionElement === null) {
          return activeIndex;
        }

        return getSectionScrollTop(scrollContainer, sectionElement) <=
          scrollContainer.scrollTop + 80
          ? index
          : activeIndex;
      },
      0
    );

    setActiveSubsectionIndex((currentIndex) =>
      currentIndex === nextActiveSubsectionIndex ? currentIndex : nextActiveSubsectionIndex
    );
  }, [activeSection, getSectionScrollTop, params.sectionIdPrefix]);

  const { entity, sections } = params;

  /**
   * Set as soon as the section is changed through the UI, which is what stops
   * the preference below from being re-applied on top of the user's own
   * navigation.
   */
  const hasNavigatedRef = React.useRef(false);

  const handleSectionChange = React.useCallback(
    (index: number) => {
      hasNavigatedRef.current = true;
      setActiveSectionIndex(index);
      setActiveSubsectionIndex(0);

      const sectionLabel = sections[index]?.label;

      // Recorded on every change, not only while the "last" option is selected,
      // so turning that option on later has somewhere to resume from.
      if (entity !== undefined && sectionLabel !== undefined) {
        setLastOpenedSection(entity, sectionLabel);
      }

      window.requestAnimationFrame(() => {
        contentRef.current?.scrollTo({ top: 0 });
      });
    },
    [entity, sections]
  );

  /**
   * Applies the "open window on section" preference.
   *
   * This re-runs whenever the section list changes rather than firing once,
   * because the list is built from the profile: the first render produces the
   * permission-only sections and the profile-dependent ones (Billing, and
   * anything gated on the entity's status) appear a moment later. A one-shot
   * would resolve against that first, incomplete list and fall back to the
   * default section. `hasNavigatedRef` is what keeps it from overriding the
   * user afterwards.
   */
  React.useEffect(() => {
    if (hasNavigatedRef.current || entity === undefined || sections.length === 0) {
      return;
    }

    const targetLabel = resolveInitialSection(
      entity,
      sections.map((section) => section.label)
    );

    if (targetLabel === undefined) {
      return;
    }

    const targetIndex = sections.findIndex((section) => section.label === targetLabel);

    if (targetIndex > 0) {
      setActiveSectionIndex(targetIndex);
      setActiveSubsectionIndex(0);
    }
  }, [entity, sections]);

  const handleSubsectionChange = React.useCallback(
    (index: number) => {
      const subsection = activeSection?.subsections[index];

      if (subsection === undefined) {
        return;
      }

      setActiveSubsectionIndex(index);

      window.requestAnimationFrame(() => {
        const scrollContainer = contentRef.current;
        const sectionElement = document.getElementById(
          `${params.sectionIdPrefix}-${subsection.id}`
        );

        if (scrollContainer === null || sectionElement === null) {
          return;
        }

        scrollContainer.scrollTo({
          top: getSectionScrollTop(scrollContainer, sectionElement),
        });
      });
    },
    [activeSection, getSectionScrollTop, params.sectionIdPrefix]
  );

  const handleContentScroll = React.useCallback(() => {
    if (scrollFrameRef.current !== null) {
      return;
    }

    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      syncActiveSubsectionWithScroll();
    });
  }, [syncActiveSubsectionWithScroll]);

  React.useEffect(
    () => () => {
      if (scrollFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
    },
    []
  );

  React.useEffect(() => {
    if (activeSection !== undefined) {
      return;
    }

    setActiveSectionIndex(0);
    setActiveSubsectionIndex(0);
  }, [activeSection]);

  return {
    activeSection,
    activeSectionIndex,
    activeSubsectionIndex,
    contentRef,
    handleContentScroll,
    handleSectionChange,
    handleSubsectionChange,
  };
}

export { useEntityProfileSectionNavigation };
export default useEntityProfileSectionNavigation;
